import type { LeadsListResponse, LeadDetailResponse, Lead, LeadStatus, LeadContactType } from "@/types";

import { atom } from "jotai";
import { atomWithQuery } from "jotai-tanstack-query";

import { apiClient } from "@/utils/api";
import { buildApiParams } from "@/utils/helpers";
import { createPaginationParams, createTableQueryOptions } from "@/components/table";

// API Base Path
const LEADS_BASE = '/leads';

// Shared params atom for leads list
export type LeadsListParams = {
  page: number;
  per_page: number;
  filters: Record<string, string | undefined>;
  sort?: string;
  refresh?: number;
};

export const leadsListParamsAtom = atom<LeadsListParams>({
  ...createPaginationParams(),
  filters: {},
  sort: undefined,
});

// Hydration flag to delay initial query until URL/filters are synced
export const leadsHydratedAtom = atom<boolean>(false);

// GET /leads list query
export const leadsQueryAtom = atomWithQuery<LeadsListResponse>((get) => {
  const params = get(leadsListParamsAtom);
  const hydrated = get(leadsHydratedAtom);

  return createTableQueryOptions({
    queryKey: ["leads", params] as const,
    queryFn: async () => {
      const apiParams = buildApiParams(
        { page: params.page, per_page: params.per_page, sort: params.sort },
        params.filters || {}
      );

      const { data } = await apiClient.get(LEADS_BASE, { params: apiParams });

      return data as LeadsListResponse;
    },
    enabled: hydrated
  });
});

// GET /leads/{id} detail query
export const leadDetailQueryAtom = atomWithQuery<LeadDetailResponse>((get) => {
  const leadId = get(leadDetailIdAtom);
  const includeRaw = get(leadDetailIncludeRawAtom);
  const refresh = get(leadDetailRefreshAtom);

  return createTableQueryOptions({
    queryKey: ["lead", leadId, includeRaw, refresh] as const,
    queryFn: async () => {
      if (!leadId) {
        throw new Error("Lead ID is required");
      }

      const params = includeRaw ? { include_raw: true } : {};
      const { data } = await apiClient.get(`${LEADS_BASE}/${leadId}`, { params });

      return data as LeadDetailResponse;
    },
    enabled: Boolean(leadId)
  });
});

// Atom for lead detail ID
export const leadDetailIdAtom = atom<number | null>(null);

// Atom for including raw data in detail query
export const leadDetailIncludeRawAtom = atom<boolean>(false);
export const leadDetailRefreshAtom = atom<number>(0);

// Atom for lead IDs (for viewer page)
export const leadsViewerIdsAtom = atom<number[]>([]);

// GET /leads query by IDs (for viewer page)
export const leadsViewerQueryAtom = atomWithQuery<LeadsListResponse>((get) => {
  const leadIds = get(leadsViewerIdsAtom);

  return createTableQueryOptions({
    queryKey: ["leads", "viewer", leadIds] as const,
    queryFn: async () => {
      if (leadIds.length === 0) {
        return { 
          success: true, 
          message: '', 
          data: [], 
          meta: { current_page: 1, last_page: 1, per_page: 15, total: 0 } 
        } as LeadsListResponse;
      }

      try {
        // Fetch leads individually (most reliable method)
        // This ensures we get all leads even if filter[ids] is not supported
        const leadPromises = leadIds.map((id) =>
          apiClient.get(`${LEADS_BASE}/${id}`).catch((err) => {
            console.warn(`Failed to fetch lead ${id}:`, err);
            return null;
          })
        );

        const responses = await Promise.all(leadPromises);
        const fetchedLeads = responses
          .filter((response): response is NonNullable<typeof response> => response !== null && response?.data?.data !== undefined)
          .map((response) => {
            // Response format is { data: Lead } from LeadDetailResponse
            return response.data.data as Lead;
          });

        // Sort leads to match the order of IDs
        const sortedLeads = leadIds
          .map((id) => fetchedLeads.find((lead) => lead.id === id))
          .filter((lead): lead is Lead => lead !== undefined);

        return {
          success: true,
          message: '',
          data: sortedLeads,
          meta: {
            current_page: 1,
            last_page: 1,
            per_page: sortedLeads.length,
            total: sortedLeads.length
          }
        } as LeadsListResponse;
      } catch (error) {
        console.error('Error fetching leads by IDs:', error);
        // Return empty result on error
        return { 
          success: true, 
          message: '', 
          data: [], 
          meta: { current_page: 1, last_page: 1, per_page: 15, total: 0 } 
        } as LeadsListResponse;
      }
    },
    enabled: leadIds.length > 0
  });
});

// Assign or clear owner for lead
export const assignLeadOwner = atom(
  null,
  async (get, set, { leadId, ownerId }: { leadId: number; ownerId: number | null }) => {
    await apiClient.post(`${LEADS_BASE}/${leadId}/assign-owner`, {
      owner_id: ownerId
    });

    // Trigger refresh by updating params
    const currentParams = get(leadsListParamsAtom);
    set(leadsListParamsAtom, { ...currentParams, refresh: Date.now() });

    // Invalidate detail query if it's for this lead
    const detailId = get(leadDetailIdAtom);
    if (detailId === leadId) {
      set(leadDetailRefreshAtom, Date.now());
    }
  }
);

// Assign lead to current user
export const assignLeadToSelf = atom(
  null,
  async (get, set, { leadId }: { leadId: number }) => {
    await apiClient.post(`${LEADS_BASE}/${leadId}/assign-to-me`);

    const currentParams = get(leadsListParamsAtom);
    set(leadsListParamsAtom, { ...currentParams, refresh: Date.now() });

    const detailId = get(leadDetailIdAtom);
    if (detailId === leadId) {
      set(leadDetailRefreshAtom, Date.now());
    }
  }
);

// Update lead status
export const updateLeadStatus = atom(
  null,
  async (get, set, { leadId, status }: { leadId: number; status: LeadStatus }) => {
    await apiClient.post(`${LEADS_BASE}/${leadId}/status`, {
      status
    });

    // Trigger refresh by updating params
    const currentParams = get(leadsListParamsAtom);
    set(leadsListParamsAtom, { ...currentParams, refresh: Date.now() });

    // Invalidate detail query if it's for this lead
    const detailId = get(leadDetailIdAtom);
    if (detailId === leadId) {
      set(leadDetailRefreshAtom, Date.now());
    }
  }
);

// Create lead note
export const createLeadNote = atom(
  null,
  async (get, set, { leadId, note }: { leadId: number; note: string }) => {
    await apiClient.post(`${LEADS_BASE}/${leadId}/notes`, {
      note
    });

    // Trigger refresh by updating params
    const currentParams = get(leadsListParamsAtom);
    set(leadsListParamsAtom, { ...currentParams, refresh: Date.now() });

    // Invalidate detail query if it's for this lead
    const detailId = get(leadDetailIdAtom);
    if (detailId === leadId) {
      set(leadDetailRefreshAtom, Date.now());
    }
  }
);

// Delete lead note
export const deleteLeadNote = atom(
  null,
  async (get, set, { leadId, noteId }: { leadId: number; noteId: number }) => {
    await apiClient.delete(`${LEADS_BASE}/${leadId}/notes/${noteId}`);

    // Trigger refresh by updating params
    const currentParams = get(leadsListParamsAtom);
    set(leadsListParamsAtom, { ...currentParams, refresh: Date.now() });

    // Invalidate detail query if it's for this lead
    const detailId = get(leadDetailIdAtom);
    if (detailId === leadId) {
      set(leadDetailRefreshAtom, Date.now());
    }
  }
);

// Create lead interaction
export const createLeadInteraction = atom(
  null,
  async (get, set, { leadId, type, note, outcome }: { leadId: number; type: string; note: string; outcome?: string | null }) => {
    await apiClient.post(`${LEADS_BASE}/${leadId}/interactions`, {
      type,
      note,
      outcome: outcome || null
    });

    // Trigger refresh by updating params
    const currentParams = get(leadsListParamsAtom);
    set(leadsListParamsAtom, { ...currentParams, refresh: Date.now() });

    // Invalidate detail query if it's for this lead
    const detailId = get(leadDetailIdAtom);
    if (detailId === leadId) {
      set(leadDetailRefreshAtom, Date.now());
    }
  }
);

// Delete lead interaction
export const deleteLeadInteraction = atom(
  null,
  async (get, set, { leadId, interactionId }: { leadId: number; interactionId: number }) => {
    await apiClient.delete(`${LEADS_BASE}/${leadId}/interactions/${interactionId}`);

    // Trigger refresh by updating params
    const currentParams = get(leadsListParamsAtom);
    set(leadsListParamsAtom, { ...currentParams, refresh: Date.now() });

    // Invalidate detail query if it's for this lead
    const detailId = get(leadDetailIdAtom);
    if (detailId === leadId) {
      set(leadDetailRefreshAtom, Date.now());
    }
  }
);

// Create lead contact
export const createLeadContact = atom(
  null,
  async (
    get,
    set,
    { leadId, type, value, source }: { leadId: number; type: LeadContactType; value: string; source?: string }
  ) => {
    const payload: { type: LeadContactType; value: string; source?: string } = {
      type,
      value
    };

    if (source) {
      payload.source = source;
    }

    await apiClient.post(`${LEADS_BASE}/${leadId}/contacts`, payload);

    const currentParams = get(leadsListParamsAtom);
    set(leadsListParamsAtom, { ...currentParams, refresh: Date.now() });

    const detailId = get(leadDetailIdAtom);
    if (detailId === leadId) {
      set(leadDetailRefreshAtom, Date.now());
    }
  }
);

// Delete lead contact
export const deleteLeadContact = atom(
  null,
  async (get, set, { leadId, contactId }: { leadId: number; contactId: number }) => {
    await apiClient.delete(`${LEADS_BASE}/${leadId}/contacts/${contactId}`);

    const currentParams = get(leadsListParamsAtom);
    set(leadsListParamsAtom, { ...currentParams, refresh: Date.now() });

    const detailId = get(leadDetailIdAtom);
    if (detailId === leadId) {
      set(leadDetailRefreshAtom, Date.now());
    }
  }
);

// Batch update leads (status and/or assign to current user)
export const batchUpdateLeads = atom(
  null,
  async (
    get,
    set,
    {
      leadIds,
      status,
      assignToMe
    }: { leadIds: number[]; status?: LeadStatus; assignToMe?: boolean }
  ) => {
    const payload: { lead_ids: number[]; status?: LeadStatus; assign_to_me?: boolean } = {
      lead_ids: leadIds
    };

    if (status) {
      payload.status = status;
    }

    if (assignToMe) {
      payload.assign_to_me = true;
    }

    await apiClient.post(`${LEADS_BASE}/batch-update`, payload);

    // Trigger refresh by updating params
    const currentParams = get(leadsListParamsAtom);
    set(leadsListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

